import {
  cloneElement,
  forwardRef,
  useEffect,
  useState,
  KeyboardEvent,
} from "react";
import { useMergedRefs } from "../../common/hooks";
import { A11yHighlight } from "../a11y-highlight";
import { useFocus } from "./hooks";
import { A11yBaseProps } from "./types";

export const A11yBase = forwardRef<HTMLElement, A11yBaseProps>(
  (
    { children, className, hasDisabledA11yFocus, onKeyDown, onAfterBlur },
    ref
  ) => {
    const { currentRef, mergedRefs } = useMergedRefs(ref);

    const { focused } = useFocus(currentRef);

    const [isMounted, setIsMounted] = useState(false);

    const hasFocus = focused && !hasDisabledA11yFocus;

    useEffect(() => {
      if (!hasFocus && isMounted) {
        onAfterBlur?.();
      } else {
        setIsMounted(true);
      }
    }, [hasFocus]);

    const handleKeyDownChildren = (event: KeyboardEvent<HTMLElement>) => {
      if (hasFocus) {
        onKeyDown(event);
      }

      children.props.onKeyDown?.(event);
    };

    return (
      <A11yHighlight hasA11yFocus={hasFocus} className={className}>
        {cloneElement(children, {
          ...children.props,
          ref: mergedRefs,
          tabIndex: hasDisabledA11yFocus ? -1 : 0,
          onKeyDown: handleKeyDownChildren,
        })}
      </A11yHighlight>
    );
  }
);

A11yBase.displayName = "A11yBase";
